// ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — single source of per-row "can I act?" semantics.
//
// Pure, testable. Turns a Markets board row into a clear actionability verdict that the
// board uses for: the row "Why?" explanation, stale/partial muting, and the summary counts.
// It NEVER makes a row look more actionable than the fail-closed BUYDEF ladder allows:
// stale / partial / blocked rows can never be Actionable, and BUY CANDIDATE can never
// appear on a stale or partial row. No order/broker path; advisory only.

import { resolveBuyCandidate, BUY_CANDIDATE_MAX_EXTENSION_PCT } from './buyCandidatePolicy';

export type ActionCategory =
  | 'Actionable' | 'Watchlist' | 'Review' | 'Stale' | 'Partial' | 'Blocked';

export type ActionTone = 'green' | 'green-soft' | 'orange' | 'red' | 'gray';

export interface ActionabilityRow {
  price?: number | string | null;
  breakout_trigger?: number | string | null;
  breakdown_trigger?: number | string | null;
  integrity_band?: string | null;
  integrity_status?: string | null;
  integrity_decision_band?: string | null;
  integrity_missing_facts?: string[] | null;
  integrity_fact_coverage?: { missing_groups?: string[] } | null;
  catalyst_status?: string | null;
  dq_flags?: string[] | null;
  calc_mismatch?: boolean | null;
  instrument_class?: string | null;
  bias?: string | null;
  risk_band?: string | null;
  quote_freshness?: string | null;
  data_quality?: string | null;
  signal_use?: string | null;
}

export interface Actionability {
  category: ActionCategory;
  tone: ActionTone;
  isActionable: boolean;       // true ONLY for category 'Actionable'
  actionStatus: string;        // human one-liner
  primaryBlocker: string;      // the single most important reason it is/ isn't actionable
  missingFacts: string[];      // human-readable missing fact names
  freshness: string;           // 'fresh' | 'stale'
  catalyst: string;            // 'verified' | 'investigating' | 'unverified' | '—'
  breakout: string;            // breakout/breakdown positioning, plain English
}

function _num(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
}
function _up(v: any): string { return String(v === null || v === undefined ? '' : v).toUpperCase().trim(); }

const _STALE_DQ = new Set(['STALE', 'QUOTE_STALE', 'FEED_DIVERGENCE', 'DATA_STALE']);

function _isStale(r: ActionabilityRow): boolean {
  const dq = (Array.isArray(r.dq_flags) ? r.dq_flags : []).map(_up);
  if (dq.some((f) => _STALE_DQ.has(f))) return true;
  const qf = String(r.quote_freshness || r.data_quality || '').toLowerCase();
  return qf.includes('stale');
}

export function missingFactNames(r: ActionabilityRow): string[] {
  const cov = r.integrity_fact_coverage || {};
  const groups = Array.isArray(cov.missing_groups) ? cov.missing_groups.map(String) : [];
  const facts = Array.isArray(r.integrity_missing_facts) ? r.integrity_missing_facts.map(String) : [];
  const all = [...groups, ...facts].map((x) => x.toLowerCase());
  const out: string[] = [];
  const add = (s: string) => { if (out.indexOf(s) === -1) out.push(s); };
  for (const v of all) {
    if (/market[_ -]?cap|valuation/.test(v)) add('market cap');
    else if (/float|shares/.test(v)) add('float');
    else if (/rvol|relative[_ -]?volume|^volume$|volume/.test(v)) add('relative volume');
    else if (/quote|fresh|liquidity|spread/.test(v)) add('quote freshness');
    else if (/bars|history/.test(v)) add('price history');
    else if (/catalyst/.test(v)) add('catalyst verification');
  }
  return out;
}

function _catalystState(r: ActionabilityRow): string {
  const c = _up(r.catalyst_status);
  if (c === 'CATALYST_VERIFIED' || c === 'VERIFIED') return 'verified';
  if (c.indexOf('INVESTIG') !== -1 || c.indexOf('PENDING') !== -1 || c.indexOf('SEARCH') !== -1) return 'investigating';
  if (!c) return '—';
  return 'unverified';
}

function _breakoutState(r: ActionabilityRow): string {
  const price = _num(r.price);
  const bo = _num(r.breakout_trigger);
  if (bo === null || price === null) return 'no breakout trigger';
  const cap = bo * (1 + BUY_CANDIDATE_MAX_EXTENSION_PCT);
  if (price < bo) return 'below trigger (not in entry window)';
  if (price > cap) return 'above trigger but beyond 3% entry cap';
  return 'above trigger, within entry window';
}

/** Single per-row actionability verdict. Precedence guarantees stale/partial/blocked
 *  are never Actionable, regardless of bias/catalyst chips. */
export function resolveActionability(r: ActionabilityRow): Actionability {
  const lad = resolveBuyCandidate(r, { maxExtensionPct: BUY_CANDIDATE_MAX_EXTENSION_PCT });
  const stale = _isStale(r);
  const fresh = stale ? 'stale' : 'fresh';
  const catalyst = _catalystState(r);
  const breakout = _breakoutState(r);
  const missing = missingFactNames(r);
  const band = _up(r.integrity_band);
  const istatus = _up(r.integrity_status);
  const risk = _up(r.risk_band);
  const partial = istatus === 'PARTIAL_FACTS' || istatus === 'INSUFFICIENT_FACTS';
  const calc = r.calc_mismatch === true || (Array.isArray(r.dq_flags) && r.dq_flags.map(_up).indexOf('CALC_MISMATCH') !== -1);
  const blocked = band === 'HIGH_RISK' || band === 'DO_NOT_TRADE' || risk === 'DO_NOT_TRADE'
    || ['high_risk', 'avoid', 'do_not_act', 'underlying_conflict', 'bias_suppressed'].indexOf(String(r.signal_use || '').toLowerCase()) !== -1;

  // precedence: safety/quality first; "actionable" only survives all gates
  let category: ActionCategory; let tone: ActionTone; let actionStatus: string; let primaryBlocker: string;
  if (stale) {
    category = 'Stale'; tone = 'gray'; actionStatus = 'Not actionable';
    primaryBlocker = 'Data stale — freshness check failed';
  } else if (calc) {
    category = 'Blocked'; tone = 'red'; actionStatus = 'Not actionable';
    primaryBlocker = 'Calculation mismatch — derived columns inconsistent';
  } else if (blocked) {
    category = 'Blocked'; tone = 'red'; actionStatus = 'Not actionable';
    primaryBlocker = band === 'DO_NOT_TRADE' ? 'Integrity DO NOT TRADE'
      : band === 'HIGH_RISK' ? 'Integrity HIGH RISK (red flags present)'
      : 'Blocked by signal policy';
  } else if (partial) {
    category = 'Partial'; tone = 'gray'; actionStatus = 'Not actionable';
    primaryBlocker = missing.length ? ('Missing ' + missing.join(', ')) : 'Required integrity facts missing';
  } else if (lad.state === 'BUY_CANDIDATE') {
    category = 'Actionable'; tone = 'green'; actionStatus = 'Actionable setup candidate (watch-only)';
    primaryBlocker = 'None — integrity CLEAR, catalyst verified, in entry window';
  } else if (lad.state === 'WATCHLIST_CANDIDATE' || lad.state === 'APPROACHING_BREAKOUT') {
    category = 'Watchlist'; tone = 'green-soft'; actionStatus = 'Watchlist — promising, not actionable yet';
    primaryBlocker = lad.reason || (lad.state === 'APPROACHING_BREAKOUT' ? 'Price below breakout trigger' : 'Extended above entry window');
  } else {
    category = 'Review'; tone = 'orange'; actionStatus = 'Review required — not actionable';
    primaryBlocker = lad.reason || 'Human review required';
  }

  return {
    category, tone, isActionable: category === 'Actionable',
    actionStatus, primaryBlocker, missingFacts: missing, freshness: fresh,
    catalyst, breakout,
  };
}

export interface BoardSummary {
  Actionable: number; Watchlist: number; Review: number;
  Stale: number; Partial: number; Blocked: number; total: number;
}

export function summarizeBoard(rows: Array<{ action?: Actionability } | ActionabilityRow>): BoardSummary {
  const s: BoardSummary = { Actionable: 0, Watchlist: 0, Review: 0, Stale: 0, Partial: 0, Blocked: 0, total: 0 };
  for (const row of rows || []) {
    const a = (row as any).action ? (row as any).action as Actionability : resolveActionability(row as ActionabilityRow);
    s[a.category] += 1; s.total += 1;
  }
  return s;
}

// Legend / glossary text — exported so the legend component AND tests share one source.
export const COLOR_LEGEND: Array<{ tone: ActionTone; label: string; meaning: string }> = [
  { tone: 'green', label: 'GREEN', meaning: 'passed / clean / favorable' },
  { tone: 'orange', label: 'ORANGE', meaning: 'caution or review required' },
  { tone: 'red', label: 'RED', meaning: 'blocked / high risk / do not act' },
  { tone: 'gray', label: 'GRAY / WHITE', meaning: 'incomplete, stale, neutral, or unknown' },
];

export const CHIP_GLOSSARY: Array<{ chip: string; meaning: string }> = [
  { chip: 'BUY CANDIDATE', meaning: 'actionable setup candidate only when integrity, catalyst, bias, freshness, and breakout timing align' },
  { chip: 'WATCHLIST CANDIDATE', meaning: 'promising but not actionable yet' },
  { chip: 'REVIEW REQUIRED', meaning: 'human review required; not actionable' },
  { chip: 'DATA STALE', meaning: 'freshness failed; not actionable' },
  { chip: 'PARTIAL', meaning: 'required data missing; not actionable' },
  { chip: 'CLEAR', meaning: 'integrity checks passed' },
  { chip: 'CAUTION', meaning: 'usable but elevated risk' },
  { chip: 'DO NOT ACT / DO NOT TRADE', meaning: 'blocked' },
  { chip: 'HIGH RISK', meaning: 'red flags present' },
];
